import { Badge } from 'features/catalog/useBadges';
import { isSoftHiddenBySiblingOffsite } from 'features/catalog/badge-definitions';
import { sortBadgeGroupsByEligibleClaimRate } from 'features/catalog/badge-groups';
import { getInitialBadgeEnabledState } from 'features/catalog/badge-state';
import { isBadgeClaimableAgainstOwnership } from 'features/claim/badge-unlock-hint';

/** Matches `groupNameForBadgeId` for badges that are not in a named group. */
export const EDIT_LAYOUT_MORE_BADGES = 'More Badges';

export type EditProfileGroupLayout<T extends { name: string; badgesIDs: string[] }> = {
  /** Groups with at least one unclaimed + not-hidden claimable badge (eligible claim-rate order). */
  claimableGroups: T[];
  /** Groups with nothing left to claim — render after More Badges. */
  deferredGroups: T[];
  deferredNames: Set<string>;
  /** claimable then deferred name order. */
  order: string[];
};

/** Group names at/above the last claim — those sections must not reshuffle. */
export type EditLayoutFrozenPrefix = {
  claimable: string[];
  deferred: string[];
  /**
   * After any claim (including top suggestion sections) use live ownership so
   * groups below the last edit can re-sort. Empty named prefixes + live = full re-sort.
   */
  live?: boolean;
};

export const EMPTY_EDIT_LAYOUT_FROZEN_PREFIX: EditLayoutFrozenPrefix = { claimable: [], deferred: [], live: false };

/** Journey / recently added / claimed-by-others — visually above every named category. */
export const EDIT_LAYOUT_TOP_SUGGESTIONS = '__top_suggestions__';

const isEmptyPrefix = (prefix: EditLayoutFrozenPrefix): boolean =>
  prefix.claimable.length === 0 && prefix.deferred.length === 0;

const usesLiveOwnership = (prefix: EditLayoutFrozenPrefix): boolean =>
  !!prefix.live || prefix.claimable.length > 0 || prefix.deferred.length > 0;

/** Keep the longer freeze when the new cut is still a prefix of (or extends) the previous cut. */
export const growNamePrefix = (prev: readonly string[], candidate: readonly string[]): string[] => {
  if (candidate.length >= prev.length && candidate.slice(0, prev.length).every((name, i) => name === prev[i])) {
    return [...candidate];
  }
  if (prev.length >= candidate.length && prev.slice(0, candidate.length).every((name, i) => name === candidate[i])) {
    return [...prev];
  }
  return prev.length > 0 ? [...prev] : [...candidate];
};

/**
 * After a claim, freeze every group at or above that group in the current layout.
 * Groups below it may re-sort. The freeze only grows so going back up cannot unstick the top.
 */
export const extendEditLayoutFrozenPrefix = <T extends { name: string; badgesIDs: string[] }>(
  layout: Pick<EditProfileGroupLayout<T>, 'claimableGroups' | 'deferredGroups'>,
  claimedGroupName: string,
  prev: EditLayoutFrozenPrefix
): EditLayoutFrozenPrefix => {
  const claimableNames = layout.claimableGroups.map((group) => group.name);
  const deferredNames = layout.deferredGroups.map((group) => group.name);
  const claimableIdx = claimableNames.indexOf(claimedGroupName);
  const deferredIdx = deferredNames.indexOf(claimedGroupName);

  // Suggestion rails sit above every named group — freeze none of them so they can all re-sort.
  if (claimedGroupName === EDIT_LAYOUT_TOP_SUGGESTIONS) {
    return { claimable: [], deferred: [], live: true };
  }
  if (claimedGroupName === EDIT_LAYOUT_MORE_BADGES || (claimableIdx < 0 && deferredIdx < 0)) {
    return {
      claimable: growNamePrefix(prev.claimable, claimableNames),
      deferred: prev.deferred,
      live: true,
    };
  }
  if (claimableIdx >= 0) {
    return {
      claimable: growNamePrefix(prev.claimable, claimableNames.slice(0, claimableIdx + 1)),
      deferred: prev.deferred,
      live: true,
    };
  }
  return {
    claimable: growNamePrefix(prev.claimable, claimableNames),
    deferred: growNamePrefix(prev.deferred, deferredNames.slice(0, deferredIdx + 1)),
    live: true,
  };
};

/**
 * Pin frozen prefix groups in place; live-sort only groups after the last claim.
 * Prefix groups stay in the claimable/deferred section they were frozen in even if
 * live eligibility would move them (that jump is what we are preventing).
 */
export const applyEditLayoutFrozenPrefix = <T extends { name: string; badgesIDs: string[] }>(
  groups: readonly T[],
  live: EditProfileGroupLayout<T>,
  prefix: EditLayoutFrozenPrefix
): EditProfileGroupLayout<T> => {
  if (isEmptyPrefix(prefix)) return live;

  const byName = new Map(groups.map((group) => [group.name, group] as const));
  const frozenClaimable = new Set(prefix.claimable);
  const frozenDeferred = new Set(prefix.deferred);

  const claimableGroups: T[] = [];
  for (const name of prefix.claimable) {
    const group = byName.get(name);
    if (group) claimableGroups.push(group);
  }
  for (const group of live.claimableGroups) {
    if (!frozenClaimable.has(group.name) && !frozenDeferred.has(group.name)) claimableGroups.push(group);
  }

  const deferredGroups: T[] = [];
  for (const name of prefix.deferred) {
    const group = byName.get(name);
    if (group) deferredGroups.push(group);
  }
  for (const group of live.deferredGroups) {
    if (!frozenClaimable.has(group.name) && !frozenDeferred.has(group.name)) deferredGroups.push(group);
  }

  return {
    claimableGroups,
    deferredGroups,
    deferredNames: new Set(deferredGroups.map((group) => group.name)),
    order: [...claimableGroups, ...deferredGroups].map((group) => group.name),
  };
};

/**
 * Person-specific edit-profile group layout from the given ownership set:
 * average claim % of unclaimed + not-hidden badges, then split deferred
 * (nothing claimable) for after More Badges.
 */
export const buildEditProfileGroupLayout = <T extends { name: string; badgesIDs: string[] }>(
  groups: readonly T[],
  badgesMap: Map<string, Badge>,
  profileBadgeIDs: readonly string[],
  submittedBadgeCount: number
): EditProfileGroupLayout<T> => {
  const profileBadgeIDSet = new Set(profileBadgeIDs);
  const baselineEnabled = getInitialBadgeEnabledState([...profileBadgeIDSet], badgesMap);
  const eligibleIdsByGroupName = new Map<string, string[]>();
  const deferredNames = new Set<string>();

  for (const group of groups) {
    const eligible: string[] = [];
    for (const id of group.badgesIDs) {
      const badge = badgesMap.get(id);
      if (!badge) continue;
      if (profileBadgeIDSet.has(id)) continue;
      if (isSoftHiddenBySiblingOffsite(id, profileBadgeIDSet)) continue;
      if (!isBadgeClaimableAgainstOwnership(badge, badgesMap, baselineEnabled, submittedBadgeCount)) continue;
      eligible.push(id);
    }
    eligibleIdsByGroupName.set(group.name, eligible);
    if (eligible.length === 0) deferredNames.add(group.name);
  }

  const sortedGroups = sortBadgeGroupsByEligibleClaimRate(groups, badgesMap, eligibleIdsByGroupName);
  const claimableGroups: T[] = [];
  const deferredGroups: T[] = [];
  for (const group of sortedGroups) {
    if (deferredNames.has(group.name)) deferredGroups.push(group);
    else claimableGroups.push(group);
  }

  return {
    claimableGroups,
    deferredGroups,
    deferredNames,
    order: sortedGroups.map((group) => group.name),
  };
};

/**
 * Session-open order until the first claim, then live claim-% order for groups after
 * the frozen prefix (never reshuffle groups above the last edited category).
 */
export const resolveEditProfileGroupLayout = <T extends { name: string; badgesIDs: string[] }>(
  groups: readonly T[],
  badgesMap: Map<string, Badge>,
  baselineOwnedIds: readonly string[],
  liveOwnedIds: readonly string[],
  submittedBadgeCount: number,
  prefix: EditLayoutFrozenPrefix
): EditProfileGroupLayout<T> => {
  const ownedIds = usesLiveOwnership(prefix) ? liveOwnedIds : baselineOwnedIds;
  return applyEditLayoutFrozenPrefix(
    groups,
    buildEditProfileGroupLayout(groups, badgesMap, ownedIds, submittedBadgeCount),
    prefix
  );
};
